import makeStyles from '@mui/styles/makeStyles';
import React,{useState} from "react";
import {
    AccordionDetails,
    Avatar,
    Accordion,
    AccordionSummary,
    IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {TextItem} from "../../components/TextItem";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RankAwardModal from "../../components/modals/RankAwardModal";
import ConfirmIconButton from "../../components/buttons/ConfirmIconButton";
import {AWARDS,deleteElem,getErrorMessage,post,put,RANKINGS} from "../../services/Client";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import NTMXGrid from "../../components/NTMXGrid";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {useTranslation} from "react-i18next";
import { useAwardTypeNameByTypeOrdinal } from 'constants/awardType';

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(1),
    },
    header: {
        fontWeight: "bold",
    },
    image: {
        height: "4rem",
        width: "4rem",
        marginRight: theme.spacing(2),
    },
    expanded: {
        backgroundColor: theme.palette.primary.dark
    },
    textItem: {
        color: "white",
        borderColor: theme.palette.primary.light,
        height: "100%",
    },
    winnersTable: {
        margin: theme.spacing(2),
        height: "18rem",
    }
}));

// un premio classifica è "libero" (rimovibile) se non è assegnato/vinto:
// nessun vincitore (uid), nessun coupon (couponStatus) e nessun timestamp di vincita.
// I tre viaggiano insieme nel dominio; con onAdd che ora manda un payload pulito,
// un premio libero non ha davvero nessuno dei tre.
const isRemovable = (a) => !a.uid && a.couponStatus == null && !a.timestamp;

export function AwardPill({award, editable, rankId, onClone}) {

    const {t} = useTranslation();
    let classes = useStyles();
    let [expanded, setExpanded] = useState(false);
    let [editing, setEditing] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    const awardTypeName = useAwardTypeNameByTypeOrdinal(award?.type ?? undefined);

    let title;
    if (expanded) title = "";
    else {
        title = award.name.substring(0,45);
        if(award.name.length > 45) title += "...";
    }

    // Accetta un id singolo (il "-" toglie un premio libero) o la lista di id del gruppo.
    const onDelete = (awardIds) => {
        const ids = [].concat(awardIds);
        let errors = 0;
        // si attende la fine di TUTTE le richieste: così "eliminato" compare solo se
        // nessuna è fallita e la lista viene invalidata una volta sola
        Promise.all(ids.map(awardId =>
            deleteElem(AWARDS +"/"+RANKINGS+"/"+awardId)
                .catch(e => {
                    enqueueSnackbar(getErrorMessage(e),{variant : "error"})
                    errors++
                })
        ))
            .then(() => errors === 0 && enqueueSnackbar(t('removed'), {variant: "success"}))
            .finally(() => queryClient.invalidateQueries([AWARDS,{rid : rankId}]))
    }

    const onEditEnd = (newValues) => {

        delete newValues.positionType
        delete newValues.groupedAwards
        delete newValues.id     // l'id di destinazione arriva dal path, non dal body

        // la modifica si applica a tutti i premi del gruppo (per un premio singolo è uno solo)
        let errors = 0;
        Promise.all(award.groupedAwards.map(a =>
            put(AWARDS +"/"+RANKINGS+"/"+a.id,{body: newValues})
                .catch(e => {
                    enqueueSnackbar(getErrorMessage(e),{variant : "error"})
                    errors++
                })
        ))
            .then(() => errors === 0 && enqueueSnackbar(t('saved'), {variant: "success"}))
            .finally(() => queryClient.invalidateQueries([AWARDS,{rid : rankId}]))
    }

    /**
     * Crea un ulteriore premio con le stesse caratteristiche 
     * dell'award in input, a differenza di prima
     * viene ripulito dei campi di audit / vincita / coupon ereditati dall'award
     * e viene creato semplicemente il premio con le stesse specifiche del premio visualizzato
     */
    const onAdd = () => {
        const {
            id, uid, username, user, timestamp, couponStatus, coupon, amount, groupedAwards,
            ...cleanCopy
        } = award;
        post(AWARDS +"/"+RANKINGS, {body: cleanCopy})
            .then(() => enqueueSnackbar(t('saved'), {variant: "success"}))
            .catch(e => enqueueSnackbar(getErrorMessage(e),{variant : "error"}))
            .finally(() => queryClient.invalidateQueries([AWARDS,{rid : rankId}]))
    }

    function RemoveButton () {
        if(award.amount > 1){
            // toglie un premio LIBERO/RIMOVIBILE del gruppo (non il primo, che potrebbe essere già vinto);
            // se sono tutti vinti il "-" è disabilitato
            const removableAward = (award.groupedAwards || []).find(isRemovable);
            return (
                <IconButton
                    disabled={!removableAward}
                    onClick={() => removableAward && onDelete(removableAward.id)}
                    size="large">
                    <RemoveIcon className={classes.iconButton}/>
                </IconButton>
            );
        } else {
            return <ConfirmIconButton
                onConfirm={() => onDelete(award.id)}
                title={t('confirmDeletion')}
                text={t('requestDefinitiveEliminationAward') + " \"" + award.name +"\""}
            >
                <RemoveIcon className={classes.iconButton}/>
            </ConfirmIconButton>
        }
    }

    function WinnersTable(){
        // un premio per riga, con il rispettivo vincitore preso dal premio stesso:
        // il DTO espone username (non "user"/"users", che non sono mai esistiti)
        const rows = award.groupedAwards.map((a, i) => ({id: i, user: a.username || t('notAssigned')}));
        const columns = [
            {
                headerName: t('award'),
                field: 'id',
                width: 200,
                valueGetter: (params) => params.value +1
            },
            {
                headerName: t('winners'),
                field: 'user',
                width: 200,
            },
        ]

        return <NTMXGrid key={"ranking-winners-table"} columns={columns} rows={rows} disableToolbar density="compact" height={"18rem"} defaultPageSize={5}/>
    }

    return (
        <Accordion
            TransitionProps={{unmountOnExit: true}}
            classes={{root: classes.root, expanded: classes.expanded}}
            square
            onChange={(e, expanded) => setExpanded(expanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon color={expanded ? "secondary" : "primary"}/>}
                aria-controls="panel1a-content"
                id={award.id}
            >
                <Grid alignItems={"center"} container direction="row">
                    <Grid xs={2}>
                        <Avatar src={award.imageUrl} alt="awardImage" className={classes.image}>
                            <EmojiEventsIcon fontSize={"large"}/>
                        </Avatar>
                    </Grid>
                    <Grid xs={3}>
                        <Typography color={"textPrimary"} className={classes.header}>{award.position ? award.position + "° " + t('position') : t('positions') + " " + award.range}</Typography>
                    </Grid>
                    <Grid xs={7}>
                        <Typography color={"textPrimary"} className={classes.header}>{title}</Typography>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container alignItems={"center"}>
                    <TextItem value={award.name} className={classes.textItem} label={t('name')} xs={12} color={"primary"}/>
                    <TextItem value={award.description} className={classes.textItem} label={t('description')}  xs={12} color={"primary"}/>
                    {
                        (award.type === 0 || award.type === 1 || award.type === 3 || award.type === 5) &&
                        <TextItem value={award.value} className={classes.textItem} xs={12} color={"primary"}
                                  label={award.type === 1 ? t('pointsValue') : t('euroValue')}/>
                    }
                    {
                        <TextItem 
                            className={classes.textItem} 
                            xs={12} 
                            color={"primary"}
                            value={awardTypeName}
                            label={t('awardType')} 
                        />
                    }
                    {award.range && <Grid container xs={6}>
                            <Grid item xs={editable ? 8 : 12}>
                                <TextItem xs={12} md={12} value={award.amount} className={classes.textItem} label={t('awardsNumber')}  color={"primary"}/>
                            </Grid>
                            {
                                editable && <Grid container xs={4} alignContent={"flex-end"}>
                                    <RemoveButton/>
                                    <IconButton onClick={onAdd} size="large">
                                        <AddIcon className={classes.iconButton}/>
                                    </IconButton>
                                </Grid>
                            }
                        </Grid>
                    }
                    {
                        award.range && <Grid container xs={12} className={classes.winnersTable}>
                                <WinnersTable/>
                        </Grid>
                    }
                    {
                        award.position && <TextItem value={award.username || t('notAssigned')} className={classes.textItem} label={t('user')} xs={12}/>
                    }
                    {
                        editable && <Grid item xs={12} style={{textAlign : "right"}}>
                            {onClone &&
                                <IconButton onClick={onClone} size="large" aria-label="duplicate">
                                    <ContentCopyIcon/>
                                </IconButton>
                            }
                            <IconButton onClick={() => setEditing(true)} size="large">
                                <EditIcon/>
                            </IconButton>
                            <ConfirmIconButton
                                onConfirm={() => onDelete(award.groupedAwards.map(a => a.id))}
                                title={t('confirmDeletion')}
                                text={t('requestDefinitiveEliminationAward') + " \"" + award.name +"\""}
                            >
                                <DeleteIcon className={classes.iconButton}/>
                            </ConfirmIconButton>
                        </Grid>
                    }
                </Grid>
            </AccordionDetails>
            <RankAwardModal open={!!editing} onClose={() => setEditing(false)} onSubmit={onEditEnd} defaultValues={award} isEditing/>
        </Accordion>
    );
}
